import { useContext, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getProducts, deleteProduct, getCategories, getAssetUrl, exportProducts, importProducts } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SearchInput from "../components/UI/SearchInput";
import SelectBox from "../components/UI/SelectBox";
import ConfirmModal from "../components/UI/ConfirmModal";
import ImportExportBar from "../components/UI/ImportExportBar";
import { notify } from "../utils/notify";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { I18nContext, useTranslation } from "../context/I18nContext";

export default function Products() {
  // Product/category names can come back translated per the signed-in
  // user's own language (server-side resolution, see ResolveEntityNames in
  // entity_translation_handler.go) — both fetches below need to re-run
  // whenever languageCode changes, or a language switch would silently
  // keep showing names resolved under the PREVIOUS language until the next
  // full page reload.
  const { languageCode } = useContext(I18nContext);
  const { t } = useTranslation("products");
  const { t: tCommon } = useTranslation("common");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(8);
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "asc" });
  const [modalConfig, setModalConfig] = useState({ isOpen: false, id: null });
  const [status, setStatus] = useState("idle");

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchProducts = async () => {
    try {
      const data = await getProducts(token, categoryFilter ? { category_id: categoryFilter } : undefined);
      setProducts(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchProducts(); }, [categoryFilter, languageCode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { getCategories(token).then(setCategories).catch(() => {}); }, [languageCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const categoryOptions = [{ value: "", label: t("filter_all_categories", "All categories") }, ...categories.map((c) => ({ value: String(c.id), label: c.name }))];

  const sortedAndFiltered = useMemo(() => {
    let result = [...products].filter((p) =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.barcode?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [products, searchTerm, sortConfig]);

  const currentItems = sortedAndFiltered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.ceil(sortedAndFiltered.length / perPage) || 1;

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const handleDeleteClick = (id) => setModalConfig({ isOpen: true, id });

  const confirmDelete = async () => {
    try {
      setStatus("loading");
      await deleteProduct(token, modalConfig.id);
      setStatus("success");
      setTimeout(() => {
        setModalConfig({ isOpen: false, id: null });
        setStatus("idle");
      }, 1200);
      notify.success(t("notify_deleted", "Product deleted successfully"));
      fetchProducts();
    } catch (err) {
      setStatus("error");
      notify.error(err.data?.error || err.message || t("notify_delete_failed", "Failed to delete product"));
    }
  };

  const currency = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const columns = [
    { key: "id", label: tCommon("table_col_id", "ID"), className: "text-gray-400 font-mono" },
    {
      key: "name",
      label: t("col_name", "Product"),
      render: (row) => (
        <div className="flex items-center gap-3 font-bold">
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 overflow-hidden shrink-0">
            {row.image_url ? (
              <img
                src={getAssetUrl(row.image_url)}
                alt={row.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <ICONS.Products className="w-4 h-4" />
            )}
          </div>
          <div>
            <div>{row.name}</div>
            <div className="text-[10px] text-gray-400 font-mono font-normal">{row.sku}</div>
          </div>
        </div>
      ),
    },
    { key: "category", label: t("col_category", "Category"), className: "text-gray-600", render: (row) => row.category?.name || "—" },
    {
      key: "price",
      label: t("col_price", "Price"),
      className: "text-gray-700 font-mono",
      render: (row) => {
        if (row.discount_source === "bundle") {
          return (
            <div className="flex items-center gap-1.5">
              <span>{currency(row.price)}</span>
              <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-purple-50 text-purple-600">{t("badge_bundle", "Bundle")}</span>
            </div>
          );
        }
        if (row.discounted_price != null && row.discounted_price < row.price) {
          const badgeLabel = row.discount_source === "sale" ? t("badge_sale", "Sale") : row.discount_source === "quantity" ? t("badge_qty", "Qty") : t("badge_discount", "Discount");
          return (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-gray-400 line-through text-xs">{currency(row.price)}</span>
              <span className="font-bold text-green-600">{currency(row.discounted_price)}</span>
              <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-green-50 text-green-600">{badgeLabel}</span>
            </div>
          );
        }
        return currency(row.price);
      },
    },
    {
      key: "tax_group",
      label: t("col_tax", "Tax"),
      render: (row) =>
        row.tax_group?.name ? (
          <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-blue-50 text-blue-600">{row.tax_group.name}</span>
        ) : (
          <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-gray-100 text-gray-500">{t("tax_exempt", "Exempt")}</span>
        ),
    },
    {
      key: "track_inventory",
      label: t("col_tracks_stock", "Tracks Stock"),
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${row.track_inventory ? "bg-blue-50 text-blue-600" : "bg-gray-100 text-gray-500"}`}>
          {row.track_inventory ? tCommon("btn_yes", "Yes") : tCommon("btn_no", "No")}
        </span>
      ),
    },
    {
      key: "is_active",
      label: tCommon("table_col_status", "Status"),
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${row.is_active ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"}`}>
          {row.is_active ? tCommon("status_active", "Active") : tCommon("status_inactive", "Inactive")}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title={t("delete_confirm_title", "Delete Product")}
        message={t("delete_confirm_message", "Are you sure? This will fail if the product still has inventory records — deactivate it instead.")}
        onConfirm={confirmDelete}
        onCancel={() => setModalConfig({ isOpen: false, id: null })}
        isLoading={status === "loading"}
        isSuccess={status === "success"}
        isError={status === "error"}
      />

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Products size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Products")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Every sellable item, retail or restaurant")}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <SearchInput
            value={searchTerm}
            onChange={(val) => { setSearchTerm(val); setCurrentPage(1); }}
            placeholder={t("search_placeholder", "Search products...")}
          />
          <ImportExportBar
            entityLabel="Products"
            fileBaseName="products"
            onExport={() => exportProducts(token)}
            onImport={(file) => importProducts(token, file)}
            onImported={fetchProducts}
          />
          <Button variant="primary" icon={ICONS.Plus} onClick={() => navigate("/add-product")} className="w-full sm:w-auto shrink-0">
            {t("btn_add_product", "Add Product")}
          </Button>
        </div>
      </PageHeader>

      <div className="max-w-xs mb-4">
        <SelectBox
          label={t("filter_by_category_label", "Filter by Category")}
          options={categoryOptions}
          value={categoryFilter}
          onChange={(val) => { setCategoryFilter(val); setCurrentPage(1); }}
        />
      </div>

      <DataTable
        columns={columns}
        data={currentItems}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={(row, isMobile) => (
          <>
            <Button variant="outline" size="sm" icon={ICONS.EditUser} onClick={() => navigate(`/edit-product/${row.id}`)}>{isMobile && ` ${tCommon("btn_edit", "Edit")}`}</Button>
            <Button variant="danger" size="sm" icon={ICONS.DeleteUser} onClick={() => handleDeleteClick(row.id)}>{isMobile && ` ${tCommon("btn_delete", "Delete")}`}</Button>
          </>
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">{row.sku}</span>
            <h3 className="font-bold text-gray-800">{row.name}</h3>
          </div>
        )}
        paginationConfig={{
          currentPage,
          totalPages,
          onPageChange: (p) => setCurrentPage(p),
          totalItems: sortedAndFiltered.length,
        }}
      />
    </div>
  );
}
